import { QueryResult, useQuery, OperationVariables, ApolloError } from '@apollo/client';
import GET_CURRICULUM_ASSESSMENTS from 'graph/queries/assessments/get-curriculums-assessments';
import {
  Assessment,
  Checklist,
  StoreTypename,
  BenchmarkAssessment,
  UnitAssessment,
  AssessmentType,
} from 'types';

export type CurriculumAssessmentsStoreState = {
  assessments?: Assessment[];
  checklists?: Checklist[];
  benchmarkAssessments?: BenchmarkAssessment[];
  unitAssessments?: UnitAssessment[];
  isLoading: boolean;
  error: ApolloError | undefined;
};

export type CurriculumAssessmentsStoreDispatch = {
  curriculumAssessmentsRefetch: any;
};

export type CurriculumAssessmentsStore = [
  CurriculumAssessmentsStoreState,
  CurriculumAssessmentsStoreDispatch
];

export const getDefaultCurriculumAssessmentsStoreState = (): any => ({
  __typename: StoreTypename.CURRICULUM_ASSESSMENTS,
});

const useCurriculumAssessmentsStore = (id: number): CurriculumAssessmentsStore => {
  const {
    data,
    loading,
    error,
    refetch: curriculumAssessmentsRefetch,
  }: QueryResult<{
    assessments: Assessment[];
    checklists: Checklist[];
    unitAssessments: Assessment[];
    benchmarkAssessments: Assessment[];
  }> = useQuery(GET_CURRICULUM_ASSESSMENTS, {
    variables: {
      assessmentWhere: {
        productCurriculumId: id,
      },
      checklistWhere: {
        productCurriculumId: id,
      },
    } as OperationVariables,
  });

  const benchmarkAssessments: BenchmarkAssessment[] = data?.assessments
    ? data.assessments
        .filter((assessment) => assessment.type === AssessmentType.Benchmark)
        .map((assessment) => assessment as BenchmarkAssessment)
    : [];

  const unitAssessments: UnitAssessment[] = data?.assessments
    ? data.assessments
        .filter((assessment) => assessment.type === AssessmentType.Unit)
        .map((assessment) => assessment as UnitAssessment)
    : [];

  return [
    {
      assessments: data?.assessments ?? [],
      benchmarkAssessments: benchmarkAssessments,
      unitAssessments: unitAssessments,
      checklists: data?.checklists ?? [],
      isLoading: loading,
      error,
    },
    {
      curriculumAssessmentsRefetch,
    },
  ];
};

export default useCurriculumAssessmentsStore;
